import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import { useReportSelector } from 'components/ReportPage/ReportContext';
import { StatusBadgeWrapperStyled } from 'components/StatusBadge/styles';
import Panel from '../../../Panels';
import Record from '../Record';

import { orderByKey } from '../lib/sort';
import { formatSearchTitle } from '../lib/screenings';
import StatusBadge from '../../../StatusBadge/StatusBadge';
import StatusBadgeWithInfo from '../../../StatusBadge/StatusBadgeWithInfo';
import StatusBadgeInfoContext from '../../../StatusBadge/StatusBadgeInfoContext';
import { getScreeningStatusAndAssessment, hasAssessment } from '../utils';

import candidateFacingStatus from '../candidateFacingStatus';

const Search = ({
  search,
  type,
  hideSuspended,
  assessmentEnabled,
  candidateFacingStatusEnabled,
}) => {
  const accountId = useReportSelector(state => state?.account?.id);
  const Badge = useContext(StatusBadgeInfoContext)
    ? StatusBadgeWithInfo
    : StatusBadge;

  const { searchStatus, statusType } = getScreeningStatusAndAssessment(
    assessmentEnabled,
    false,
    search,
  );
  const candidateStatus = candidateFacingStatus(
    searchStatus,
    candidateFacingStatusEnabled,
  );

  const {
    county,
    state,
    country,
    records,
    filteredByPositiveAdjudicationRecords,
  } = search;

  if (!county && !state && !country) {
    return null;
  }

  if (search.status === 'canceled') {
    return <div />;
  }

  const searchTitle = formatSearchTitle(search, accountId);

  // don't display the search if the search is suspended
  if (
    (hideSuspended && search.status === 'suspended') ||
    search.status === 'canceled'
  ) {
    return <div />;
  }

  const recordsToDisplay = (records || []).concat(
    filteredByPositiveAdjudicationRecords || [],
  );

  return (
    <div className='mb-3' data-testid='search-collection-item'>
      <div className='d-flex mb-2'>
        <div>
          <strong>{searchTitle}</strong>
        </div>
        <div className='ml-auto'>
          <StatusBadgeWrapperStyled>
            {candidateStatus && (
              <Badge status={candidateStatus} statusType={statusType} />
            )}
          </StatusBadgeWrapperStyled>
        </div>
      </div>
      <div>
        {recordsToDisplay.map((record, i) => (
          <Record
            key={`record-${i + 1}`}
            record={record}
            index={i}
            type={type}
            assessmentEnabled={assessmentEnabled && hasAssessment(search)}
          />
        ))}
      </div>
    </div>
  );
};

Search.propTypes = {
  type: PropTypes.string,
  search: PropTypes.object,
  hideSuspended: PropTypes.bool,
  assessmentEnabled: PropTypes.bool,
  candidateFacingStatusEnabled: PropTypes.bool,
};

Search.defaultProps = {
  type: '',
  search: {},
  hideSuspended: false,
  assessmentEnabled: false,
  candidateFacingStatusEnabled: false,
};

const SearchCollection = ({
  dataTestId,
  searches,
  title,
  type,
  hideSuspended,
  assessmentEnabled,
  useScreeningStatusOverAssessment,
  status,
  candidateFacingStatusEnabled,
}) => {
  const { searchStatus, statusType, secondaryStatus } =
    getScreeningStatusAndAssessment(
      assessmentEnabled,
      useScreeningStatusOverAssessment,
      searches,
      status,
    );

  const collectionStatus = candidateFacingStatus(
    searchStatus,
    candidateFacingStatusEnabled,
  );
  const collectionSecondaryStatus = candidateFacingStatus(
    secondaryStatus,
    candidateFacingStatusEnabled,
  );

  if (!searches || !searches.length) {
    return null;
  }

  const orderedSearches = orderByKey(searches, 'status', [
    'pending',
    'consider',
    'suspended',
    'complete',
    'clear',
  ]);

  if ((hideSuspended && status === 'suspended') || status === 'canceled') {
    return <div />;
  }

  return (
    <Panel
      data-testid={dataTestId}
      title={title}
      status={collectionStatus}
      statusType={statusType}
      secondaryStatus={collectionSecondaryStatus}
    >
      {orderedSearches.map((search, i) => (
        <Search
          key={`search-${i + 1}`}
          search={search}
          type={type}
          hideSuspended={hideSuspended}
          assessmentEnabled={assessmentEnabled}
          candidateFacingStatusEnabled={candidateFacingStatusEnabled}
        />
      ))}
    </Panel>
  );
};

SearchCollection.propTypes = {
  searches: PropTypes.array,
  title: PropTypes.string,
  type: PropTypes.string,
  hideSuspended: PropTypes.bool,
  dataTestId: PropTypes.string,
  assessmentEnabled: PropTypes.bool,
  useScreeningStatusOverAssessment: PropTypes.bool,
  status: PropTypes.string,
  candidateFacingStatusEnabled: PropTypes.bool,
};

SearchCollection.defaultProps = {
  searches: [],
  title: '',
  type: '',
  hideSuspended: false,
  dataTestId: '',
  assessmentEnabled: false,
  useScreeningStatusOverAssessment: false,
  status: null,
  candidateFacingStatusEnabled: false,
};

export default SearchCollection;
